/**
 * safeDate.js — timezone / cross-engine safe date parsing for display.
 *
 * WHY THIS EXISTS (cross-browser-qa-rule, settings-account §15.7 "Date parse in billing"):
 *   `new Date("2026-08-14")` — a date-ONLY ISO string — is parsed as UTC midnight by every
 *   spec-compliant engine. Rendered back with a LOCAL formatter (date-fns `format`,
 *   `toLocaleDateString`), a viewer in any negative-UTC zone (all of the Americas) sees the
 *   PREVIOUS calendar day. Measured: TZ=America/Los_Angeles,
 *     format(new Date("2026-08-14"), "MM/dd/yy") -> "08/13/26"   (WRONG, off by one)
 *     same input in UTC                          -> "08/14/26"   (correct)
 *   Non-ISO strings ("2026/08/14", "08-14-2026") are additionally parsed inconsistently across
 *   engines (Chrome is lenient; older Safari/Firefox return Invalid Date).
 *
 * parseLocalDate() removes the ambiguity: a bare YYYY-MM-DD is pinned to LOCAL midnight of that
 * exact calendar day, so the displayed day never shifts with the viewer's timezone or engine.
 * Anything carrying a time component (an ISO datetime with T/Z, a Date, an epoch) is passed
 * straight through to the native Date, whose instant is unambiguous.
 */

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parse a raw date value into a Date that displays the intended calendar day in the viewer's
 * local timezone, regardless of timezone or JS engine.
 * @param {string|number|Date} raw
 * @returns {Date|null} a valid Date, or null when the input is empty / unparseable.
 */
export function parseLocalDate(raw) {
  if (raw === null || raw === undefined || raw === "") return null;
  if (raw instanceof Date) return isNaN(raw.getTime()) ? null : raw;

  if (typeof raw === "string") {
    const m = raw.match(DATE_ONLY_RE);
    if (m) {
      // Local midnight of the exact Y-M-D — never UTC, so the day cannot shift by timezone.
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      return isNaN(d.getTime()) ? null : d;
    }
  }

  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

export default parseLocalDate;
